import React, { useState } from 'react';

import { downloadUrl } from '@apis/common';
import { FormTitle } from '@components/admin/Forms';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { PromptLeave } from '@components/Forms/PromptLeave';
import { TextInput } from '@components/Forms/TextInput';
import { LoadingArea } from '@components/LoadingArea';
import { SpinnerModal } from '@components/SpinnerModal';
import { VerticalSpace } from '@components/VerticalSpace';
import { agencyStrings } from '@data/agency';
import { usePatchCompany } from '@hooks/mutation/dashboard/usePatchCompany';
import { usePatchCompanyMemberNo } from '@hooks/mutation/dashboard/usePatchCompanyMemberNo';
import { usePostCompanySalesperson } from '@hooks/mutation/dashboard/usePostCompanySalesperson';
import { usePostK01 } from '@hooks/mutation/dashboard/usePostK01';
import { useCompanyDelegation } from '@hooks/queries/dashboard/useCompanyDelegation';
import { useCompanyDetail } from '@hooks/queries/dashboard/useCompanyDetail';
import { useCompanyItem } from '@hooks/queries/dashboard/useCompanyItem';
import { useCompanyMemberNo } from '@hooks/queries/dashboard/useCompanyMemberNo';
import { useK01 } from '@hooks/queries/useK01';
import { useUser } from '@hooks/queries/useUser';
import { allCompanyStatus } from '@models/Company';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, formStrings, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime } from '@utils/types';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    infoManagement: '管理情報',
    edit: '編集',
    id: '申し込み番号',
    createdAt: '申し込み時間',
    status: '対応状況',
    admissionDate: '入会時間',
    completeApplication: '入会手続き完了',
    assignee: '営業担当者',
    becomeAssignee: '担当者になる',
    evaluation: '会員総合評価',
    connectStem: 'FC基幹システムと繋がる',
    export: 'エクスポート',
    createEvaluationSheet: '評価シートを作成',
    memberNo: '会員番号',
    approvalNo: '稟議番号',
    evaluationSheetK01: '評価シートK01',
    transactionConfirmationK04: '取引確認書K04',
    delegationForm: '通関委任状',
    changeSalesperson: '担当者があなたに変更されますが、進めてよろしいでしょうか。',
    statusUpdated: '対応状況を更新しました。',
    promptStatusDone: '「D2D会員開設申請」を更新したら元に戻せません。\n更新してもよろしいですか？',
    memberNoAndApprovalNoMust: '「会員番号」と「稟議番号」は必須です',
    roleUpdated: '会員種別を更新しました。',
    approvalNoPrompt: '*BA番号がない既存顧客の場合、BA00000000を入力してください',
    statusDoneDesc: (no: string) => {
      return (
        <>
          お疲れ様でした！
          <br />
          <span>
            申し込み番号
            {String(no).padStart(9, '0')}
          </span>
          のD2D会員開設申請を提出しました。
          <br />
          Adminによる審査をお待ちください。
        </>
      );
    },
    goBack: '新規会員申し込み一覧に戻る',
    goMemberDetails: '対応した会員ページへ',
    editAgency: `${agencyStrings.agency}を指定する`,
    needRoutePlans: `${agencyStrings.agency}は未指定です。`,
    statusOptions: () =>
      allCompanyStatus
        .map((value) => ({
          value,
          label: value,
        }))
        .filter((element) => !['廃棄', 'ドラフト', '手続き完了'].includes(element.value)),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  managementContainer: {
    ...common.flexColumn,
    border: '1px solid #DDDDDD',
  },
  managementCell: {
    ...common.flexColumn,
    padding: '22px 22px 16px',
    '&:not(:last-child)': {
      borderBottom: '1px solid #DDDDDD',
    },
  },
  keyValueRow: {
    ...common.flexInline,
    ...common.middleText,
    color: theme.palette.grey['700'],
    height: 36,
    '& > div': {
      ...common.flexInline,
      alignItems: 'center',
    },
  },
  managementTitle: {
    ...common.middleText,
    ...common.flexInline,
    color: theme.palette.grey['800'],
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  updatedInfo: {
    ...common.smallerText,
    color: theme.palette.primary.main,
    fontWeight: 500,
    marginTop: '0.1rem',
  },
  managementForm: {
    ...common.flexColumn,
  },
  managementButton: {
    alignSelf: 'center',
    width: 160,
    margin: '0.5rem 0',
  },
  cancelButton: {
    alignSelf: 'center',
    width: 160,
    margin: '0.5rem 0',
    backgroundColor: theme.palette.grey['500'],
    color: theme.palette.primary.contrastText,
  },
  updateButton: {
    alignSelf: 'left',
    width: 60,
    margin: '0.5rem 0',
  },
  inlineButton: {
    height: 32,
    width: 130,
    borderRadius: 16,
  },
  formButton: {
    width: 70,
    height: 32,
    borderRadius: 16,
    margin: '1.5rem 1rem',
  },
  loadingArea: {
    height: '70vh',
    border: '1px solid #DDDDDD',
  },
  inlineForm: {
    flex: 1,
    '& > * + *': {
      marginTop: '0.5rem',
    },
  },
  input: {
    ...common.smallerText,
  },
  textInput: {
    padding: 5,
  },
  menuItemRoot: {
    borderColor: theme.palette.grey['800'],
  },
  dialogText: {
    ...common.middleText,
    ...common.fontWeight400,
    ...common.textGray800,
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 30,
    '& span': {
      textDecoration: 'none',
      color: theme.palette.primary.main,
    },
  },
}));

export interface InfoManagementProps {
  id: number;
}

const memberNoSchema = t.type({
  memberNo: requiredStringField(strings.memberNo),
  approvalNo: requiredStringField(strings.approvalNo),
});

const validateMemberNo = createValidate(memberNoSchema);

export const InfoManagement: React.FC<InfoManagementProps> = (props) => {
  const { id } = props;

  const history = useHistory();
  const [showSalesperson, setShowSalesperson] = useState(false);
  const [showStatusUpdated, setShowStatusUpdated] = useState(false);
  const [statusDoneActive, setStatusDoneActive] = useState(false);

  const delegation = useCompanyDelegation(id);
  const companyItem = useCompanyItem(id);
  const companyDetail = useCompanyDetail(id);
  const memberNo = useCompanyMemberNo(id);
  const postCompanySalesperson = usePostCompanySalesperson(id);
  const patchCompanyMemberNo = usePatchCompanyMemberNo(id);
  const patchCompany = usePatchCompany(id);
  const postK01 = usePostK01(id);

  const k01 = useK01(id);

  const user = useUser();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isUpdating = [
    postCompanySalesperson.isLoading,
    patchCompanyMemberNo.isLoading,
    patchCompany.isLoading,
    postK01.isLoading,
  ].some(Boolean);

  if (delegation.isLoading || companyItem.isLoading || memberNo.isLoading) {
    return (
      <>
        <FormTitle title={strings.infoManagement} noEdit />
        <LoadingArea classes={{ container: classes.loadingArea }} />
      </>
    );
  }

  const companyItemData = companyItem.data!;
  const companyDetailData = companyDetail.data!;
  const memberNoData = memberNo.data!;

  const shouldInputMemberNo = ['会員番号入力待ち', '手続き完了待ち'].includes(
    companyItemData.status,
  );

  const isSalesperson = user.data?.id === companyItemData.salesperson_id;
  const isStatusDone = companyItemData.status === '手続き完了';
  const onOpenK01 = () => {
    window.open(k01.data!.edit_link);
  };

  const onProcessK01 = async () => {
    await postK01.mutateAsync();
    onOpenK01();
  };

  const onClickK04 = async () => {
    await downloadUrl({
      url: `/dashboard/company/${id}/k04/`,
      name: 'k04.pdf',
      type: 'application/pdf',
    });
  };

  const onClickK01 = async () => {
    await downloadUrl({
      url: `/dashboard/company/${id}/k01/download/`,
      name: 'k01.xlsx',
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  };

  const renderStatusDoneModal = () => (
    <Dialog open={statusDoneActive}>
      <div className={classes.dialogText}>{strings.statusDoneDesc(`${id}`)}</div>
      <br />
      <Button
        color="primary"
        text={strings.goBack}
        style={{ alignSelf: 'center', width: 250 }}
        onClick={() => {
          setStatusDoneActive(false);
          history.push(url('/admin/members/MembershipForms'));
        }}
      />
    </Dialog>
  );

  const renderKeyValue = (key: string, value: React.ReactNode) => {
    return (
      <div className={classes.keyValueRow}>
        <div style={{ flex: 1 }}>{key}</div>
        <div style={{ flex: 1, letterSpacing: -1 }}>{value}</div>
      </div>
    );
  };

  const renderSalesperson = () => {
    return (
      <>
        {renderKeyValue(strings.assignee, companyItemData.salesperson_name)}
        {!isSalesperson && (
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.becomeAssignee}
            onClick={() => setShowSalesperson(true)}
          />
        )}
        <Dialog open={showSalesperson}>
          <div className={classes.dialogText}>{strings.changeSalesperson}</div>
          <Button
            color="custom"
            shape="round"
            size="small"
            className={classes.cancelButton}
            text={formStrings.cancel}
            onClick={() => setShowSalesperson(false)}
            type="submit"
          />
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={formStrings.yes}
            onClick={() => {
              postCompanySalesperson.mutateAsync();
              setShowSalesperson(false);
            }}
            type="submit"
          />
        </Dialog>
      </>
    );
  };

  const renderConnectStem = () => {
    return (
      <Formik
        initialValues={{
          memberNo: process.env.NODE_ENV === 'development' ? 'FCS41922' : memberNoData.member_no,
          approvalNo:
            process.env.NODE_ENV === 'development' ? 'BA21080014' : memberNoData.approval_no,
        }}
        enableReinitialize
        validate={validateMemberNo}
        onSubmit={async (values) => {
          const validation = memberNoSchema.decode(values);

          if (!isRight(validation)) {
            throw new Error('InfoManagement: Unexpected erroneus form values. ');
          }

          try {
            await patchCompanyMemberNo.mutateAsync({
              member_no: values.memberNo,
              approval_no: values.approvalNo,
            });
          } catch (e) {
            if (e instanceof FormError) {
              // eslint-disable-next-line no-alert
              alert(e.dict.message);
            }
            throw e;
          }
        }}
      >
        <Form className={classes.managementForm}>
          <PromptLeave title={formStrings.confirm} message={formStrings.promptLeaving} />
          <div className={classes.managementTitle}>{strings.connectStem}</div>

          <TextInput
            label={strings.memberNo}
            name="memberNo"
            itemVariant="column"
            itemErrorVariant="inline"
            formClasses={{
              root: classes.inlineForm,
            }}
            classes={{
              input: classes.input,
              textInput: classes.textInput,
            }}
            disabled={!shouldInputMemberNo || !isSalesperson}
          />

          <TextInput
            label={strings.approvalNo}
            name="approvalNo"
            itemVariant="column"
            itemErrorVariant="inline"
            formClasses={{
              root: classes.inlineForm,
            }}
            classes={{
              input: classes.input,
              textInput: classes.textInput,
            }}
            disabled={!shouldInputMemberNo || !isSalesperson}
          />
          <div style={{ height: 3 }} />
          <div className={clsx(classes.textGray500, classes.smallerText)}>
            {strings.approvalNoPrompt}
          </div>

          <div style={{ height: 7 }} />

          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={formStrings.confirm}
            type="submit"
            disabled={!shouldInputMemberNo || !isSalesperson}
          />
        </Form>
      </Formik>
    );
  };

  const renderStatus = () => {
    return (
      <Formik
        initialValues={{
          status: companyDetailData.status,
        }}
        enableReinitialize
        validate={createValidate(
          t.type({
            status: requiredStringField(strings.status),
          }),
        )}
        onSubmit={async (values) => {
          const nextStatus = values.status;

          if (
            (!memberNoData.member_no || !memberNoData.approval_no) &&
            ['手続き完了待ち', 'D2D会員開設申請'].includes(nextStatus)
          ) {
            // eslint-disable-next-line no-alert
            alert(strings.memberNoAndApprovalNoMust);
            return;
          }

          // eslint-disable-next-line no-alert, no-restricted-globals
          if (nextStatus === 'D2D会員開設申請' && !confirm(strings.promptStatusDone)) {
            return;
          }

          try {
            await patchCompany.mutateAsync({
              status: values.status,
            });
            setShowStatusUpdated(true);

            if (nextStatus === 'D2D会員開設申請') {
              setStatusDoneActive(true);
            }
            setTimeout(() => setShowStatusUpdated(false), 5000);
          } catch (e) {
            if (e instanceof FormError) {
              // eslint-disable-next-line no-alert
              alert(e.getDisplayMessage());
            }
            throw e;
          }
        }}
      >
        {() => (
          <Form className={classes.managementForm}>
            <div className={classes.managementTitle}>
              {strings.status}
              {showStatusUpdated && (
                <>
                  <div style={{ width: '1rem' }} />
                  <div className={classes.updatedInfo}>{strings.statusUpdated}</div>
                </>
              )}
            </div>
            <TextInput
              options={strings.statusOptions()}
              noLabel
              name="status"
              classes={{
                input: classes.input,
                textInput: classes.textInput,
              }}
              itemVariant="column"
              itemErrorVariant="column"
              choose
              disabled={!isSalesperson || isStatusDone}
            />
            <VerticalSpace height={10} />
            <Button
              color="primary"
              shape="round"
              size="small"
              className={classes.updateButton}
              text={formStrings.update}
              type="submit"
              disabled={!isSalesperson || isStatusDone}
            />
          </Form>
        )}
      </Formik>
    );
  };

  return (
    <>
      <FormTitle title={strings.infoManagement} noEdit />
      <SpinnerModal open={isUpdating} />
      <div className={classes.managementContainer}>
        <div className={classes.managementCell}>
          {renderKeyValue(strings.id, String(companyItemData.approval_no ?? ''))}
          {renderKeyValue(strings.createdAt, formatDateTime(companyItemData.created_at))}
          <div style={{ height: 8 }} />
        </div>

        <div className={classes.managementCell}>{renderSalesperson()}</div>

        <div className={classes.managementCell}>
          <div className={classes.managementTitle}>{strings.evaluation}</div>
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.createEvaluationSheet}
            onClick={onProcessK01}
            disabled={k01.data === undefined || !isSalesperson}
          />
        </div>

        <div className={classes.managementCell}>{renderConnectStem()}</div>

        <div className={classes.managementCell}>{renderStatus()}</div>

        <div className={classes.managementCell}>
          <div className={classes.managementTitle}>{strings.export}</div>

          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.evaluationSheetK01}
            onClick={onClickK01}
            disabled={k01.data === undefined}
          />
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.transactionConfirmationK04}
            onClick={onClickK04}
          />
          <Button
            color="primary"
            shape="round"
            size="small"
            className={classes.managementButton}
            text={strings.delegationForm}
            disabled={!delegation.data?.file}
            onClick={() => window.open(delegation.data!.file!)}
          />
        </div>
      </div>
      {renderStatusDoneModal()}
    </>
  );
};
